import React, { useCallback, useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { Dialog, DialogContent } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { historicalChartModalStyles } from './styles';
import storeConnector from '@store/storeConnector';
import { useChartTimezoneConfig } from '@utils/timezoneUtils';

import ChartContent from './ChartContent';
import ModalHeader from './ModalHeader';
import ChartPortal from './ChartPortal';

import { useHistoricalData } from '../../hooks/useHistoricalData';
import { useChartModalState } from '../../hooks/useChartModalState';

import {
  calculateInitialChartDates,
  calculateInterval,
  calculateStatistics,
  TimeInterval,
} from '../../utils/temperatureUtils';
import {
  calculateChartYAxisMax,
  type ChartData,
  findClosestDataPoint,
  getCurrentTemperatureFromData,
  transformDataToChartFormat,
} from '../../utils/chartUtils';
import { createChartOptions } from './chartConfig';
import { SiteMeta } from '@src/types/SiteMeta';
import { TimeMode } from '@src/types';

dayjs.extend(utc);
dayjs.extend(timezone);

interface ChartModalProps {
  siteMeta?: SiteMeta;
  timeMode?: TimeMode;
}

const ChartModal: React.FC<ChartModalProps> = ({
  siteMeta,
  timeMode = TimeMode.UTC,
}) => {
  const theme = useTheme();
  const { modalState, closeModal } = useChartModalState();

  // Move all hooks before the early return
  const timezoneConfig = useChartTimezoneConfig(timeMode, siteMeta!);
  const { currentTimeZoneString, tzOffset } = timezoneConfig;

  // Use safe default values when incident is not available
  const incident = modalState.incident;
  const defaultTimestamp = new Date().toISOString();
  const initialDates = calculateInitialChartDates(
    incident?.timestamp || defaultTimestamp,
  );

  const [dateFrom, setDateFrom] = useState<string>(initialDates.dateFrom);
  const [dateTo, setDateTo] = useState<string>('');
  const [interval, setInterval] = useState<TimeInterval>(initialDates.interval);

  const calculateIntervalFromDates = useCallback(
    (from: dayjs.Dayjs, to: dayjs.Dayjs): TimeInterval => {
      return calculateInterval(from.valueOf(), to.valueOf());
    },
    [],
  );

  useEffect(() => {
    if (modalState.isOpen && incident) {
      const now = dayjs.utc();
      let from: dayjs.Dayjs;

      if (incident.timestamp) {
        // Start from 2 hours before the incident
        from = dayjs.utc(incident.timestamp).subtract(2, 'hours');
      } else {
        from = now.subtract(2, 'hours');
      }

      const to = now;
      const calculatedInterval = calculateIntervalFromDates(from, to);

      setDateFrom(from.format('YYYY-MM-DDTHH:mm:ss[Z]'));
      setDateTo(to.format('YYYY-MM-DDTHH:mm:ss[Z]'));
      setInterval(calculatedInterval);
    }
  }, [
    modalState.isOpen,
    incident?.timestamp,
    calculateIntervalFromDates,
    incident,
  ]);

  const { data, isLoading, isFetching, error } = useHistoricalData({
    siteId: siteMeta?.siteId || '',
    unitId: incident?.unitId || '',
    rackId: incident?.rackId || '',
    dateFrom,
    dateTo,
    interval,
    enabled: modalState.isOpen && !!incident,
  });

  const handleRefresh = useCallback(() => {
    const now = dayjs.utc();
    const from = dayjs.utc(dateFrom);
    const calculatedInterval = calculateIntervalFromDates(from, now);

    setDateTo(now.format('YYYY-MM-DDTHH:mm:ss[Z]'));
    setInterval(calculatedInterval);
  }, [dateFrom, calculateIntervalFromDates]);

  const chartData = useMemo<ChartData>(() => {
    return transformDataToChartFormat(data);
  }, [data]);

  const yAxisMax = useMemo(() => {
    return calculateChartYAxisMax(chartData);
  }, [chartData]);

  const statistics = useMemo(() => calculateStatistics(chartData), [chartData]);

  const currentTemperature = useMemo(() => {
    return getCurrentTemperatureFromData(data, incident?.maxCellT || 0);
  }, [data, incident?.maxCellT]);

  const incidentPoint = useMemo(() => {
    return findClosestDataPoint(chartData, incident?.timestamp || '');
  }, [chartData, incident?.timestamp]);

  const chartOptions = useMemo(
    () =>
      createChartOptions({
        theme,
        chartData,
        deviceName: incident?.deviceName || '',
        rackId: incident?.rackId || '',
        currentTemperature,
        yAxisMax,
        currentTimeZoneString,
        tzOffset,
        dateFrom,
        dateTo,
        interval,
        incidentPoint,
        siteMeta,
        timeMode,
      }),
    [
      theme,
      chartData,
      incident?.deviceName,
      incident?.rackId,
      currentTemperature,
      yAxisMax,
      currentTimeZoneString,
      tzOffset,
      dateFrom,
      dateTo,
      interval,
      incidentPoint,
      siteMeta,
      timeMode,
    ],
  );

  // Early return if modal is not open or no incident data
  if (!modalState.isOpen || !incident) {
    return null;
  }

  return (
    <ChartPortal>
      <Dialog
        open={modalState.isOpen}
        onClose={closeModal}
        maxWidth='lg'
        fullWidth
        PaperProps={{
          sx: historicalChartModalStyles.dialogPaper,
        }}
      >
        <ModalHeader
          incidentTimestamp={incident.timestamp}
          onRefresh={handleRefresh}
          onClose={closeModal}
          isFetching={isFetching}
        />
        <DialogContent dividers>
          <ChartContent
            isLoading={isLoading}
            isFetching={isFetching}
            error={error}
            data={data}
            statistics={statistics}
            currentTemperature={currentTemperature}
            chartOptions={chartOptions}
            onRefresh={handleRefresh}
          />
        </DialogContent>
      </Dialog>
    </ChartPortal>
  );
};

ChartModal.displayName = 'ChartModal';

const ChartModalWithStore = storeConnector(ChartModal, {
  config: ['siteMeta'],
  service: ['timeMode'],
});

export default ChartModalWithStore;
